import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, IconSpinner, ResponsiveImage } from '..';

import css from './TalentListingCard.module.css';
import IconArrowTop from '../IconArrowTop/IconArrowTop';
import Icons from '../Icons/Icons';
import { getTalentAttribute } from '../../util/listingDataExtraction';
import {
  ProfileDetails,
  SchoolAttributes,
  SocialMediaPlatform,
  TalentAttributes,
} from '../../util/enums';
import { formatNumberCompact, isArrayLength } from '../../util/genericHelpers';
import {
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../ducks/user.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../util/routes';
const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice} From $10
      </div>
      {/* {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null} */}
    </div>
  );
};

export const TalentListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    isLandingListingCard,
    openFavoritesModal,
    setFirstImage,
    hideFavorite,
    isSectionSchoolHero = false,
    isSectionAgencyHero = false,
  } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const currentUser = useSelector(userSelector);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUserId = currentUser?.id?.uuid;
  const { favorites = [], removedItemId } = useSelector(favoritesSelector);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const favoritesList = favorites || [];
  const listingId = listing?.id?.uuid;
  const isOwnListing = currentUserId && listing?.author?.id?.uuid === currentUserId;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const isAlreadyAddedToFavoriteslist =
    isArrayLength(favoritesList) &&
    favoritesList.some(category => category.ids?.includes(listingId));
  const handleFavoritesList = async e => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isAlreadyAddedToFavoriteslist) {
        await dispatch(
          deleteItemFromFavorites({
            isAuthenticated,
            id: listingId,
            dispatch,
            favorites,
            config,
            isAuthenticated,
            userId: currentUserId,
          })
        );
      } else {
        openFavoritesModal(listingId);
        setFirstImage(firstImage);
      }
    } catch (error) {
      console.log('Error while updating favorites');
    }
  };
  const { INSTAGRAM, SOCIAL_MEDIA, TIKTOK, TWITTER } = SocialMediaPlatform;
  const { PROFILE_DETAILS } = TalentAttributes;
  const { SCHOOL_DISPLAYNAME } = SchoolAttributes;
  const platformIconMap = {
    tiktok: TIKTOK,
    twitter: TWITTER,
    instagram: INSTAGRAM,
    default: SOCIAL_MEDIA,
  };
  const classes = classNames(rootClassName || css.root, className, {
    [css.landingListingCard]: isLandingListingCard,
    [css.sectionSchoolHero]: isSectionSchoolHero || isSectionAgencyHero,
  });

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-medium',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const listingDescription = currentListing?.attributes?.description;
  const listingProfileDetails = getTalentAttribute(currentListing, PROFILE_DETAILS);

  const sportsOptions = isArrayLength(publicData?.pub_sports) ? publicData?.pub_sports : null;

  const listingSocialMediaDetails =
    (isArrayLength(listingProfileDetails) &&
      listingProfileDetails?.filter(profile => profile.type === ProfileDetails.SOCIAL_MEDIA)) ||
    [];
  const navigateToListing = () => {
    history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
  };
  return (
    <>
      <div className={classes} onClick={navigateToListing}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          {/* <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} /> */}
          {!isOwnListing && !hideFavorite && (
            <span
              className={classNames(css.favorite, isAlreadyAddedToFavoriteslist && css.favorited)}
              onClick={e => {
                e.stopPropagation();
                isAuthenticated
                  ? handleFavoritesList(e)
                  : history.push(
                      createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
                    );
              }}
            >
              {removedItemId === listingId ? <IconSpinner /> : <Icons name="star" />}
            </span>
          )}
        </AspectRatioWrapper>

        <div className={css.info}>
          <div className={css.cardPostTime}>
            {isArrayLength(sportsOptions)
              ? sportsOptions.map((s, i) => (
                  <span className={css.time} key={i}>
                    {s?.label}
                  </span>
                ))
              : null}
          </div>
          <div className={css.cardTitleSec}>
            <h2 className={css.cardTitle}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </h2>
            <IconArrowTop />
          </div>
          <p className={css.cardDescription}>{listingDescription}</p>
          <div className={css.followers}>
            {Array.isArray(listingSocialMediaDetails) && listingSocialMediaDetails.length > 0
              ? listingSocialMediaDetails.map(({ followers, handle, platform }, index) => {
                  const iconName = platformIconMap[platform] || platformIconMap.default;
                  const formattedFollowers = formatNumberCompact(followers);

                  return (
                    <span key={`${platform}-${handle}-${index}`} className="social-media-item">
                      {iconName && <Icons name={iconName} />}
                      {formattedFollowers && ` ${formattedFollowers}`}{' '}
                    </span>
                  );
                })
              : null}
          </div>
        </div>
      </div>
    </>
  );
};

TalentListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

TalentListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(TalentListingCardComponent);
